/* Change password (2026-10-27). An agency onboarded from the console gets a
 * password made by the server and handed over by the platform team; the owner
 * replaces it here. POST /me/password with the current one: the server checks
 * it, stores the new one, and signs out every OTHER session of this account. */

import { useState } from 'react';
import { ApiError, api } from '../data/api';
import { Btn, Field, Modal, Note, PasswordInput, uiCss as css } from '../ui';

export function ChangePassword({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const close = () => {
    setCurrent('');
    setNext('');
    setAgain('');
    setError(null);
    setDone(false);
    onClose();
  };

  const blocked = !current
    ? 'Type your current password'
    : next.length < 8
      ? 'The new password needs at least 8 characters'
      : next !== again
        ? 'The two new passwords are not the same'
        : next === current
          ? 'Choose a password different from the current one'
          : null;

  const save = async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    try {
      await api('/me/password', { method: 'POST', body: JSON.stringify({ current_password: current, password: next }) });
      setDone(true);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'INVALID_CREDENTIALS'
          ? 'Your current password is not right.'
          : e instanceof Error
            ? e.message
            : 'The password could not be changed.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={close} label="Change password">
      <div className={css.modalHead}>
        <div className={css.modalTitle}>Change password</div>
        <div className={css.modalDesc}>
          Your sign-in for this desk. Other places signed in to this account are signed out when it changes.
        </div>
      </div>
      <div className={css.modalBody}>
        {done ? (
          <Note tone="ok">Your password is changed. Use the new one next time you sign in.</Note>
        ) : (
          <>
            <Field label="Current password">
              <PasswordInput value={current} onChange={setCurrent} ariaLabel="Current password" autoComplete="current-password" />
            </Field>
            <Field label="New password (at least 8 characters)">
              <PasswordInput value={next} onChange={setNext} ariaLabel="New password" autoComplete="new-password" />
            </Field>
            <Field label="New password again">
              <PasswordInput value={again} onChange={setAgain} ariaLabel="New password again" autoComplete="new-password" />
            </Field>
            {error ? <Note tone="danger">{error}</Note> : null}
            {blocked && (current || next || again) ? <span className={css.sub}>{blocked}.</span> : null}
          </>
        )}
      </div>
      <div className={css.modalFoot}>
        {done ? (
          <Btn variant="primary" onClick={close}>
            Done
          </Btn>
        ) : (
          <>
            <Btn onClick={close}>Cancel</Btn>
            <Btn variant="primary" disabled={!!blocked || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Change password'}
            </Btn>
          </>
        )}
      </div>
    </Modal>
  );
}
